import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { ModalController, Platform } from '@ionic/angular';
import { PrinterGroup } from 'src/app/models/printer/printer-group.model';
import { Printer } from 'src/app/models/printer/printer.model';
import { ModalFormParentComponent } from '../modal-form-parent/modal-form-parent.component';

import { PRINTER_TYPE } from 'src/app/api/core/printer/printer.constants';
import each from 'lodash/each';
import { UtilityClass } from 'src/app/api/utils/UtilityClass';

@Component({
  selector: 'hub-modal-network-printer',
  templateUrl: './modal-network-printer.component.html',
  styleUrls: ['./modal-network-printer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalNetworkPrinterComponent extends ModalFormParentComponent implements OnInit {
  /** The form submit button */
  @ViewChild('saveBtn', { static: true, read: ElementRef }) saveBtn: ElementRef;

  /** The printer object to edit. If this empty, the function is considered adding. */
  @Input() printer: Printer;

  /** Flagger to change the state of the modal to EDIT */
  @Input() edit: boolean = false;

  /** List of available printer groups. */
  @Input() printerGroups: PrinterGroup[];

  public printerGroupForm: { group: PrinterGroup; isChecked: boolean }[] = [];

  /** Form controls for the Printer name, address and port */
  public printerName: FormControl = new FormControl('', [Validators.required]);
  public printerAddress: FormControl = new FormControl('', [Validators.required]);
  public printerPort: FormControl = new FormControl('9100', [Validators.required]);
  public passPrinter: FormControl = new FormControl(true, [Validators.required]);
  public activatePrinter: FormControl = new FormControl(true, [Validators.required]);
  public printRepeat: FormControl = new FormControl(1, [Validators.required]);

  /** Printer instruction to display */
  public printerInstruction: string = '';

  constructor(
    public platform: Platform,
    public modalCtrl: ModalController,
    public renderer: Renderer2,
    public changeRef: ChangeDetectorRef,
  ) {
    super(platform, modalCtrl, renderer);
  }

  ngOnInit() {
    this.modalHeightFitToView = true;
    super.ngOnInit();
    this.keyboardParentTarget = this.saveBtn;

    this.parentFormGroup.addControl('name', this.printerName);
    this.parentFormGroup.addControl('address', this.printerAddress);
    this.parentFormGroup.addControl('pass', this.passPrinter);
    this.parentFormGroup.addControl('active', this.activatePrinter);
    this.parentFormGroup.addControl('repeat', this.printRepeat);

    // set the correct validators for editing and adding new ethernet printer
    if (!this.edit || (this.printer && this.printer.Type == PRINTER_TYPE.IP)) {
      this.printerAddress.setValidators([Validators.required, this.isIpAddressValidator()]);
      this.parentFormGroup.addControl('port', this.printerPort);
    }

    if (this.printer) {
      // populate the field
      this.parentFormGroup.patchValue(this.printer.export());
      this.parentFormGroup.updateValueAndValidity();
    }

    if (this.printerGroups && this.printerGroups.length > 0) {
      this.printerGroupForm = this.printerGroups.map(grp => {
        let isChecked: boolean = false;
        if (this.edit) {
          if (grp.Linked_printers && grp.Linked_printers.indexOf(this.printer.Address) > -1) {
            isChecked = true;
          }
        }

        return { group: grp, isChecked: isChecked };
      });
    }

    this.changeRef.markForCheck();
  }

  /** Sync form validtor function to check if value is IP Address */
  private isIpAddressValidator(): ValidatorFn {
    return (
      control: AbstractControl,
    ): {
      [key: string]: any;
    } | null => {
      let error;
      if (!UtilityClass.isIPAddress(control.value)) {
        error = {
          invalid_value: {
            value: 'Please provide a valid IP Address',
          },
        };
      }

      return error;
    };
  }

  /** Outputs the selected printer instruction on how to retrieve device printer */
  printerSelectedChange($event) {
    if ($event && $event.detail) {
      switch ($event.detail.value) {
        case 'epson-receipt':
          this.printerInstruction = `
            <ol>
              <li>Power the printer off.</li>
              <li>Press and hold the feed button.</li>
              <li>Power the printer on and continue to hold the feed button.</li>
              <li>After a few seconds a settings sheet will print, including the IP address of the printer</li>
            </ol>
          `;
          break;
        case 'senor':
          this.printerInstruction = `
            <ol>
              <li>Make sure the Printer power switch is "OFF"</li>
              <li>Push "Paper Feed" button located on top of the printer</li>
              <li>While Holding "Paper Feed" power "ON" the printer</li>
              <li>Self test will now print please take note of the printers IP Address</li>
            </ol>
          `;
          break;
        case 'bixolon':
          this.printerInstruction = `
            <ol>
              <li>Connect the LAN cable into the B-gate and then turn on the B-gate.</li>
              <li>After the B-gate is booted, Open Printer Cover → Press and Release Feed Button → Close Printer Cover. This will print the B-gate’s Self Test Page, which includes LAN Configuration Information as well.</li>
              <li>From the Self Test Page, take a note on the value of ETH_DHCP, if the value is Disabled then, open your Host(e.g. PC) Network Configuration and set your Host in the same LAN network as the B-gate’s LAN Configuration. For example, if the B-gate’s default IP is 192.168.192.168 then, make your Host IP into 192.168.192.X (x = 1~255) and the Subnet Mask is 255.255.255.0 and Save your new setting.</li>
              <li>If the ETH_DHCP value is Enabled then just connect the LAN Cable, the B-gate will automatically acquire the IP Information from your DHCP Server(router), print the B-gate self Test Page again to get know the B-gate’s IP Information.</li>
              <li>Open the web browser on your Host and enter the B-gate’s IP in the address bar, if it is open the BGATE WebCONFIG Tool then you have configured your PC and the B-gate Correctly.</li>
            </ol>
          `;
          break;
        default:
          break;
      }
    }

    this.changeRef.detectChanges();
  }

  /** Save the new printer */
  save = () => {
    if (this.parentFormGroup.valid || this.edit) {
      let response = this.parentFormGroup.getRawValue();
      response['printerGroupForm'] = this.printerGroupForm;
      this.modalCtrl.dismiss(response, 'save');
    } else {
      this.parentFormGroup.updateValueAndValidity();
      this.changeRef.detectChanges();
    }
  };

  /** switch off All Printer Group toggles */
  vetoPrinterGroupSelection(value: boolean): void {
    if (value) {
      each(this.printerGroupForm, (g: any) => {
        g.isChecked = false;
      });
    }
  }

  /** switch off Pass Printer toggle */
  vetoPassPrinter(value: boolean): void {
    if (value) {
      this.passPrinter.setValue(false);
    }
  }

  /** Increases the number of times a docket is printed */
  public addPrintDocket(): void {
    let repeatValue: number = this.printRepeat.value;
    this.printRepeat.setValue(++repeatValue);
  }

  /** Decreases the number of times a docket is printed */
  public minusPrintDocket(): void {
    let repeatValue: number = this.printRepeat.value;
    this.printRepeat.setValue(--repeatValue);
  }

  /** Close the modal */
  close(): void {
    this.modalCtrl.dismiss();
  }
}
